import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../../firebase";
import ToolbarHeader, { GTA_GAME_TABS } from "../Community/components/ToolbarHeader";
import EmptyState from "./components/EmptyState";
import "../Community/css/ui-shell.css";
import "../Community/css/MusicRadioIndex.css";
import "./css/MusicRadioIndex.css"; // NEW: styles for row list

// put near top of MusicRadioIndex.jsx

function ensureHttps(u = "") {
  const s = String(u || "").trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  return `https://${s.replace(/^\/+/, "")}`;
}

function toSpotifyEmbed(url = "") {
  const raw = ensureHttps(url);
  try {
    // spotify:playlist:ID etc
    if (/^spotify:/i.test(raw)) {
      const [, type, id] = raw.split(":");
      if (type && id) return `https://open.spotify.com/embed/${type}/${id}`;
      return null;
    }
    const u = new URL(raw);
    if (!/open\.spotify\.com$/i.test(u.hostname)) return null;

    // /playlist/ID, /album/ID, /embed/playlist/ID ... -> /embed/<type>/<id>
    const parts = u.pathname.replace(/^\/+/, "").split("/");
    const p = parts[0] === "embed" ? parts.slice(1) : parts;
    const kind = p[0];
    const id = p[1];
    if (!kind || !id) return null;

    return `https://open.spotify.com/embed/${kind}/${id}`;
  } catch {
    return null;
  }
}

function toYouTubeEmbed(url = "") {
  const raw = ensureHttps(url);
  try {
    const u = new URL(raw);
    if (!/youtube\.com$|youtu\.be$/i.test(u.hostname)) return null;

    // playlist only
    const list = u.searchParams.get("list");
    const v = u.searchParams.get("v");

    if (list && !v) return `https://www.youtube.com/embed/videoseries?list=${list}`;

    // single video
    let id = "";
    if (u.hostname.includes("youtu.be")) id = u.pathname.slice(1);
    else if (v) id = v;
    if (!id) return null;
    return `https://www.youtube.com/embed/${id}`;
  } catch {
    return null;
  }
}

function toAppleMusicEmbed(url = "") {
  const raw = ensureHttps(url);
  try {
    const u = new URL(raw);
    if (!/music\.apple\.com$/i.test(u.hostname)) return null;
    u.hostname = "embed.music.apple.com";
    return u.toString();
  } catch {
    return null;
  }
}

function mapStationDoc(id, x = {}) {
  const cover =
    x.coverImage ||
    (Array.isArray(x.gallery) && x.gallery.length > 0 ? x.gallery[0] : "") ||
    x.image ||
    x.imageUrl ||
    "";

  const createdAtMs =
    x.createdAt?.toMillis?.() ??
    (typeof x.createdAt === "number" ? x.createdAt : 0);

  return {
    id,
    slug: x.slug || null,
    name: x.name || "Untitled Station",
    tagline: x.tagline || "",
    game: x.game || "",
    cover,
    frequency: x.frequency || "",
    city: x.city || "",
    djs: Array.isArray(x.djs) ? x.djs : [],
    genres: Array.isArray(x.genres) ? x.genres : [],
    spotifyUrl: x.spotifyUrl || "",
    youtubeUrl: x.youtubeUrl || "",
    appleMusicUrl: x.appleMusicUrl || "",
    createdAtMs,
    createdAtText: createdAtMs ? new Date(createdAtMs).toLocaleDateString() : "",
    reactions: x.reactions || {},
  };
}

export default function MusicRadioIndex() {
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get("q") || "");

  // Game tabs
  const [selectedKey, setSelectedKey] = useState("all");

  // Inline player state
  const [playingId, setPlayingId] = useState(null);

  // realtime
  useEffect(() => {
    const qStations = query(collection(db, "radio_stations"), orderBy("name"));
    const un = onSnapshot(
      qStations,
      (snap) => {
        const rows = snap.docs.map((d) => mapStationDoc(d.id, d.data()));
        setStations(rows);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => un();
  }, []);

  // sync ?q
  useEffect(() => {
    const next = {};
    if (search) next.q = search;
    setSearchParams(next, { replace: true });
  }, [search, setSearchParams]);

  // close player if filtered out
  useEffect(() => {
    if (playingId && !stations.some((s) => s.id === playingId)) {
      setPlayingId(null);
    }
  }, [stations, playingId]);

  const selectedGameLabel =
    GTA_GAME_TABS.find((g) => g.key === selectedKey)?.label || null;

  const list = useMemo(() => {
    let src = stations;

    if (selectedKey !== "all" && selectedGameLabel) {
      src = src.filter(
        (x) => (x.game || "").toLowerCase() === selectedGameLabel.toLowerCase()
      );
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      src = src.filter((x) =>
        [
          x.name,
          x.tagline || "",
          x.game || "",
          x.frequency || "",
          x.city || "",
          ...(x.djs || []),
          ...(x.genres || []),
        ]
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
    }

    const copy = [...src];
    copy.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    return copy;
  }, [stations, search, selectedKey, selectedGameLabel]);

  const hasPlayable = (s) =>
    Boolean(toSpotifyEmbed(s.spotifyUrl) || toYouTubeEmbed(s.youtubeUrl) || toAppleMusicEmbed(s.appleMusicUrl));

  const renderPlayer = (s) => {
    const sp = toSpotifyEmbed(s.spotifyUrl);
    const yt = toYouTubeEmbed(s.youtubeUrl);
    const am = toAppleMusicEmbed(s.appleMusicUrl);

    if (sp) {
      return (
        <iframe
          title={`spotify-${s.id}`}
          src={sp}
          width="100%"
          height="152"
          frameBorder="0"
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          loading="lazy"
        />
      );
    }
    if (yt) {
      return (
        <iframe
          title={`youtube-${s.id}`}
          src={yt}
          width="100%"
          height="260"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          loading="lazy"
          allowFullScreen
        />
      );
    }
    if (am) {
      return (
        <iframe
          title={`apple-${s.id}`}
          src={am}
          width="100%"
          height="190"
          frameBorder="0"
          sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation-by-user-activation"
          loading="lazy"
        />
      );
    }
    return <div className="mr-player-empty">No playable link added.</div>;
  };

  return (
    <>
      <div className="wepo-wrap" >
        <ToolbarHeader
          children={
            <div className="forum-tabs">
              {GTA_GAME_TABS.map(({ key, label }) => {
                const active = selectedKey === key;
                return (
                  <span
                    key={key}
                    className={`tab-text ${active ? "active" : ""}`}
                    onClick={() => setSelectedKey(key)}
                    role="button"
                    aria-current={active ? "page" : undefined}
                  >
                    {label}
                  </span>
                );
              })}
            </div>
          }
          search={search}
          setSearch={setSearch}
          placeholder="Search stations, DJs, genres…"
          className="vw-toolbar"
        />

        {/* Loading skeleton */}
        {loading && (
          <ul className="mr-list">
            {Array.from({ length: 6 }).map((_, i) => (
              <li key={`sk-${i}`} className="mr-row is-skeleton">
                <div className="mr-cover sk-block" />
                <div className="mr-mid">
                  <div className="sk-line sk-title" />
                  <div className="sk-line sk-meta" />
                </div>
                <div className="mr-right">
                  <div className="sk-chip" />
                </div>
              </li>
            ))}
          </ul>
        )}

        {!loading && stations.length === 0 && (
          <div className="empty-block">
            <EmptyState
              icon="🎵"
              title="No Music & Radio Stations"
              description="Please stay tuned. New stations will be added soon."
            />
          </div>
        )}


        {!loading && stations.length > 0 && (
          <ul className="mr-list">
            {list.map((s) => {
              const open = playingId === s.id;
              const playable = hasPlayable(s);

              return (
                <li key={s.id} className={`mr-row ${open ? "is-open" : ""}`}>
                  <div className="mr-cover">
                    {s.cover ? (
                      <img src={s.cover} alt="" loading="lazy" />
                    ) : (
                      <div className="mr-cover--placeholder" aria-label="No image">🎵</div>
                    )}
                  </div>

                  <div className="mr-mid">
                    <div className="mr-title-line">
                      <Link
                        to={`/community/music_radio/${s.slug || s.id}`}
                        className="mr-title"
                        title={s.name}
                      >
                        {s.name}
                      </Link>
                      {s.tagline && <span className="mr-tagline"> — {s.tagline}</span>}
                    </div>

                    <div className="mr-sub">
                      {s.game && <span className="chip">🎮 {s.game}</span>}
                      {s.frequency && <span className="chip">📻 {s.frequency}</span>}
                      {s.city && <span className="chip">📍 {s.city}</span>}
                      {s.djs?.length > 0 && <span className="chip">🎙 {s.djs[0]}</span>}
                      {Array.isArray(s.genres) &&
                        s.genres.slice(0, 3).map((t) => (
                          <span className="chip" key={t}>#{t}</span>
                        ))}
                    </div>
                  </div>

                  <div className="mr-right">
                    <button
                      className="mr-playbtn"
                      disabled={!playable}
                      aria-pressed={open}
                      aria-label={open ? "Hide player" : "Play"}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setPlayingId((p) => (p === s.id ? null : s.id));
                      }}
                      title={playable ? (open ? "Hide player" : "Play") : "No stream linked"}
                    >
                      {open ? "Pause" : "Play"}
                    </button>

                    <Link
                      className="mr-details"
                      to={`/community/music_radio/${s.slug || s.id}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      Details →
                    </Link>
                  </div>

                  {open && <div className="mr-player">{renderPlayer(s)}</div>}
                </li>
              );
            })}

            {list.length === 0 && (
              <li className="mr-empty-row">
                <div className="mr-empty">No stations match your filters.</div>
              </li>
            )}
          </ul>
        )}
      </div>
    </>
  );
}
